<template lang="pug">
aiq-dialog.advance-crawl-configuration(
  :title="$t(`${baseTranslationPath}.title`)"
  :model-value="visible"
  :show-close="true"
  :close-on-click-modal="false"
  :close-on-press-escape="true"
  width="640px"
  append-to-body
  @update:model-value="onVisibleChange"
  @closed="onClosed"
  )
  .advance-crawl-body
    //- Schedule: Options to pull periodically and automatically
    .config-section(v-if="supportSchedule")
      .section-title {{ $t(`${baseTranslationPath}.schedule`) }}
      .field-container.flex-display.gap-between
        aiq-switch.switch(v-model="localConfig.enabled")
        label {{ $t(`${baseTranslationPath}.enabled`) }}
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.frequency`) }}
        aiq-select(v-model="localConfig.frequency" :disabled="!localConfig.enabled")
          aiq-option(
            v-for="option in frequencyOptions"
            :key="option"
            :label="$t(`${baseTranslationPath}.frequencies.${option}`)"
            :value="option"
            )

    //- Source
    .config-section
      .section-title {{ $t(`${baseTranslationPath}.source`) }}
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.base_url`) }}
        aiq-input(v-model="localConfig.base_url"
                  :placeholder="$t(`${baseTranslationPath}.base_url_placeholder`)"
                  :max-length="500")
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.sitemap_url`) }}
        aiq-input(v-model="localConfig.website.sitemap_url"
                  :placeholder="$t(`${baseTranslationPath}.sitemap_url_placeholder`)"
                  :max-length="500")
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.user_agent`) }}
        aiq-input(v-model="localConfig.website.user_agent"
                  :placeholder="$t(`${baseTranslationPath}.user_agent_placeholder`)"
                  :max-length="500")
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.warmup_nav_path`) }}
        aiq-input(v-model="localConfig.website.warmup_nav_path"
                  :placeholder="$t(`${baseTranslationPath}.warmup_nav_path_placeholder`)"
                  :max-length="500")
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.extra_headers`) }}
        .header-row(v-for="(header, index) in extraHeaderRows" :key="index")
          aiq-input.header-key(v-model="header.key"
                              :placeholder="$t(`${baseTranslationPath}.header_key_placeholder`)"
                              @input="syncExtraHeaders")
          aiq-input.header-value(v-model="header.value"
                                :placeholder="$t(`${baseTranslationPath}.header_value_placeholder`)"
                                @input="syncExtraHeaders")
          aiq-button.remove-header(type="text" @click="removeHeaderRow(index)")
            fa-icon(:icon="['fas', 'trash']")
        aiq-button.add-header(:plain="true" @click="addHeaderRow") {{ $t(`${baseTranslationPath}.add_header`) }}

    //- Crawler behavior
    .config-section
      .section-title {{ $t(`${baseTranslationPath}.crawler_behavior`) }}
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.crawler_type`) }}
        aiq-select(v-model="localConfig.website.crawler_type")
          aiq-option(
            v-for="option in crawlerTypeOptions"
            :key="option"
            :label="$t(`${baseTranslationPath}.crawler_types.${option}`)"
            :value="option"
            )
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.robot_mode`) }}
        aiq-select(v-model="localConfig.website.robot_file_config.mode")
          aiq-option(
            v-for="option in robotModeOptions"
            :key="option"
            :label="$t(`${baseTranslationPath}.robot_modes.${option}`)"
            :value="option"
            )

    //- Rules
    .config-section
      .section-title {{ $t(`${baseTranslationPath}.rules`) }}
      .field-container.flex-display.gap-between
        aiq-switch.switch(v-model="localConfig.website.rules.use_sitemap")
        label {{ $t(`${baseTranslationPath}.use_sitemap`) }}
      .field-container.flex-display.gap-between
        aiq-switch.switch(v-model="localConfig.website.rules.follow_links")
        label {{ $t(`${baseTranslationPath}.follow_links`) }}
      .field-container.flex-display.gap-between
        aiq-switch.switch(v-model="localConfig.website.rules.full_resource_loading")
        label {{ $t(`${baseTranslationPath}.full_resource_loading`) }}
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.include_paths`) }}
        aiq-select(v-model="localConfig.website.rules.include_paths"
                  multiple
                  filterable
                  :allow-create="true"
                  :default-first-option="true"
                  :reserve-keyword="false"
                  :placeholder="$t(`${baseTranslationPath}.paths_placeholder`)")
      .field-container.full-width
        label {{ $t(`${baseTranslationPath}.exclude_paths`) }}
        aiq-select(v-model="localConfig.website.rules.exclude_paths"
                  multiple
                  filterable
                  :allow-create="true"
                  :default-first-option="true"
                  :reserve-keyword="false"
                  :placeholder="$t(`${baseTranslationPath}.paths_placeholder`)")
      .number-grid
        .field-container
          label {{ $t(`${baseTranslationPath}.max_pages`) }}
          aiq-input-number(v-model="localConfig.website.rules.max_pages" :min="1" :controls="false")
        .field-container
          label {{ $t(`${baseTranslationPath}.max_depth`) }}
          aiq-input-number(v-model="localConfig.website.rules.max_depth" :min="0" :controls="false")
        .field-container
          label {{ $t(`${baseTranslationPath}.page_delay_seconds`) }}
          aiq-input-number(v-model="localConfig.website.rules.page_delay_seconds" :min="0" :step="0.5" :precision="1" :controls="false")
        .field-container
          label {{ $t(`${baseTranslationPath}.js_render_wait_ms`) }}
          aiq-input-number(v-model="localConfig.website.rules.js_render_wait_ms" :min="0" :step="100" :controls="false")
        .field-container
          label {{ $t(`${baseTranslationPath}.context_pool_size`) }}
          aiq-input-number(v-model="localConfig.website.rules.context_pool_size" :min="1" :controls="false")

    //- Authentication (login wall)
    .config-section(v-if="supportLoginWall")
      .section-title {{ $t(`${baseTranslationPath}.authentication`) }}
      .field-container.flex-display.gap-between
        aiq-switch.switch(v-model="localConfig.website.auth.enabled")
        label {{ $t(`${baseTranslationPath}.auth_enabled`) }}
      template(v-if="localConfig.website.auth.enabled")
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.auth_type`) }}
          aiq-select(v-model="localConfig.website.auth.type")
            aiq-option(
              v-for="option in authTypeOptions"
              :key="option"
              :label="$t(`${baseTranslationPath}.auth_types.${option}`)"
              :value="option"
              )
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.login_url`) }}
          aiq-input(v-model="localConfig.website.auth.login_url"
                    :placeholder="$t(`${baseTranslationPath}.login_url_placeholder`)"
                    :max-length="500")
        .subsection-title {{ $t(`${baseTranslationPath}.selectors`) }}
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.selector_username`) }}
          aiq-input(v-model="localConfig.website.auth.selectors.username"
                    :placeholder="$t(`${baseTranslationPath}.selector_placeholder`)"
                    :max-length="500")
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.selector_password`) }}
          aiq-input(v-model="localConfig.website.auth.selectors.password"
                    :placeholder="$t(`${baseTranslationPath}.selector_placeholder`)"
                    :max-length="500")
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.selector_submit`) }}
          aiq-input(v-model="localConfig.website.auth.selectors.submit"
                    :placeholder="$t(`${baseTranslationPath}.selector_placeholder`)"
                    :max-length="500")
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.selector_remember_me`) }}
          aiq-input(v-model="localConfig.website.auth.selectors.remember_me"
                    :placeholder="$t(`${baseTranslationPath}.selector_placeholder`)"
                    :max-length="500")
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.selector_error_block`) }}
          aiq-input(v-model="localConfig.website.auth.selectors.error_block"
                    :placeholder="$t(`${baseTranslationPath}.selector_placeholder`)"
                    :max-length="500")
        .subsection-title {{ $t(`${baseTranslationPath}.success_check`) }}
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.success_url_contains`) }}
          aiq-input(v-model="localConfig.website.auth.success_check.url_contains"
                    :placeholder="$t(`${baseTranslationPath}.success_url_placeholder`)"
                    :max-length="500")
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.success_url_not_contains`) }}
          aiq-input(v-model="localConfig.website.auth.success_check.url_not_contains"
                    :placeholder="$t(`${baseTranslationPath}.success_url_placeholder`)"
                    :max-length="500")
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.success_selector`) }}
          aiq-input(v-model="localConfig.website.auth.success_check.selector"
                    :placeholder="$t(`${baseTranslationPath}.selector_placeholder`)"
                    :max-length="500")
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.login_wall_signals`) }}
          aiq-select(v-model="localConfig.website.auth.login_wall_signals"
                    multiple
                    filterable
                    :allow-create="true"
                    :default-first-option="true"
                    :reserve-keyword="false"
                    :placeholder="$t(`${baseTranslationPath}.login_wall_signals_placeholder`)")
        .field-container
          label {{ $t(`${baseTranslationPath}.max_login_attempts`) }}
          aiq-input-number(v-model="localConfig.website.auth.max_login_attempts" :min="1" :controls="false")
        .subsection-title {{ $t(`${baseTranslationPath}.credentials`) }}
        p.credentials-hint(v-if="localConfig.website.auth.has_credentials") {{ $t(`${baseTranslationPath}.credentials_stored`) }}
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.credential_username`) }}
          aiq-input(v-model="localCredentials.username"
                    :placeholder="$t(`${baseTranslationPath}.credential_username_placeholder`)"
                    :max-length="500")
        .field-container.full-width
          label {{ $t(`${baseTranslationPath}.credential_password`) }}
          aiq-input(v-model="localCredentials.password"
                    type="password"
                    show-password
                    :placeholder="$t(`${baseTranslationPath}.credential_password_placeholder`)"
                    :max-length="500")

  template(#footer)
    .dialog-footer
      aiq-button(@click="onCancel") {{ $t('global.cancel') }}
      aiq-button(type="primary" @click="onApply") {{ $t('global.save') }}
</template>
<script>
import cloneDeep from 'lodash/cloneDeep';

const FREQUENCY_OPTIONS = ['weekly', 'daily', 'every_2_days', 'every_3_days'];
const CRAWLER_TYPE_OPTIONS = ['http', 'headless'];
const ROBOT_MODE_OPTIONS = ['respect', 'ignore'];
const AUTH_TYPE_OPTIONS = ['form'];

// Full config template matching CrawlConfig.yaml / CrawlAuthConfig.yaml.
const buildDefaultConfig = () => ({
  enabled: true,
  base_url: null,
  frequency: 'daily',
  extra_headers: {},
  website: {
    sitemap_url: null,
    user_agent: null,
    warmup_nav_path: null,
    crawler_type: 'http',
    robot_file_config: {
      mode: 'respect',
    },
    rules: {
      use_sitemap: false,
      include_paths: [],
      exclude_paths: [],
      max_pages: 500,
      page_delay_seconds: 1.0,
      js_render_wait_ms: 2000,
      follow_links: true,
      max_depth: 4,
      context_pool_size: null,
      full_resource_loading: false,
    },
    auth: {
      enabled: false,
      type: 'form',
      login_url: '',
      selectors: {
        username: '',
        password: '',
        submit: '',
        remember_me: '',
        error_block: '',
      },
      success_check: {
        url_contains: '',
        url_not_contains: '',
        selector: '',
      },
      login_wall_signals: [],
      max_login_attempts: 3,
    },
  },
});

export default {
  name: 'AdvanceCrawlConfiguration',
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      required: true,
    },
    supportSchedule: {
      type: Boolean,
      default: true,
    },
    supportLoginWall: {
      type: Boolean,
      default: true,
    },
    credentials: {
      type: Object,
      default: () => ({ username: '', password: '' }),
    },
  },
  emits: ['update:visible', 'update:config', 'update:credentials'],
  data() {
    return {
      baseTranslationPath: 'settings_tab.smart_drive_tab.edit.advanced_crawling',
      frequencyOptions: FREQUENCY_OPTIONS,
      crawlerTypeOptions: CRAWLER_TYPE_OPTIONS,
      robotModeOptions: ROBOT_MODE_OPTIONS,
      authTypeOptions: AUTH_TYPE_OPTIONS,
      localConfig: buildDefaultConfig(),
      localCredentials: { username: '', password: '' },
      extraHeaderRows: [],
    };
  },
  watch: {
    visible(isVisible) {
      if (isVisible) {
        this.resetLocalConfig();
      }
    },
  },
  created() {
    this.resetLocalConfig();
  },
  methods: {
    resetLocalConfig() {
      this.localConfig = this.normalizeConfig(this.config);
      this.localCredentials = {
        username: '',
        password: '',
        ...(this.credentials || {}),
      };
      this.extraHeaderRows = Object.entries(this.localConfig.extra_headers || {})
        .map(([key, value]) => ({ key, value }));
    },
    // Merge the incoming config onto the full schema template so every field the
    // template binds to exists, and migrate the legacy flat shape (sitemap_url /
    // user_agent / warmup_nav_path / rules at the top level) into `website`.
    normalizeConfig(source) {
      const src = cloneDeep(source) || {};
      const base = buildDefaultConfig();

      const legacyWebsite = {};
      ['sitemap_url', 'user_agent', 'warmup_nav_path', 'rules'].forEach((key) => {
        if (src[key] !== undefined) {
          legacyWebsite[key] = src[key];
          delete src[key];
        }
      });

      const website = { ...(src.website || legacyWebsite) };
      const auth = website.auth || {};

      return {
        ...base,
        ...src,
        extra_headers: src.extra_headers || {},
        website: {
          ...base.website,
          ...website,
          robot_file_config: {
            ...base.website.robot_file_config,
            ...(website.robot_file_config || {}),
          },
          rules: {
            ...base.website.rules,
            ...(website.rules || {}),
          },
          auth: {
            ...base.website.auth,
            ...auth,
            selectors: {
              ...base.website.auth.selectors,
              ...(auth.selectors || {}),
            },
            success_check: {
              ...base.website.auth.success_check,
              ...(auth.success_check || {}),
            },
          },
        },
      };
    },
    addHeaderRow() {
      this.extraHeaderRows.push({ key: '', value: '' });
    },
    removeHeaderRow(index) {
      this.extraHeaderRows.splice(index, 1);
      this.syncExtraHeaders();
    },
    syncExtraHeaders() {
      this.localConfig.extra_headers = this.extraHeaderRows.reduce((acc, { key, value }) => {
        const trimmedKey = (key || '').trim();
        if (trimmedKey) {
          acc[trimmedKey] = value;
        }
        return acc;
      }, {});
    },
    onVisibleChange(value) {
      this.$emit('update:visible', value);
    },
    onClosed() {
      this.$emit('update:visible', false);
    },
    onCancel() {
      this.$emit('update:visible', false);
    },
    onApply() {
      this.syncExtraHeaders();
      this.$emit('update:config', cloneDeep(this.localConfig));
      this.$emit('update:credentials', cloneDeep(this.localCredentials));
      this.$emit('update:visible', false);
    },
  },
};
</script>
<style lang="scss" scoped>
.advance-crawl-body {
  max-height: 60vh;
  overflow-y: auto;
  padding-right: 6px;
}

.config-section {
  padding-bottom: 18px;
  margin-bottom: 18px;
  border-bottom: 1px solid #e4e7ed;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
  }
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2d3d;
  margin-bottom: 12px;
}

.subsection-title {
  font-size: 13px;
  font-weight: 600;
  color: #475669;
  margin-top: 16px;
}

.credentials-hint {
  color: #909399;
  font-size: 13px;
  margin: 0 0 12px;
}

.field-container {
  margin-top: 12px;

  label {
    display: block;
    margin-bottom: 6px;
    color: #475669;
    font-size: 13px;
  }
}

.full-width {
  width: 100%;

  :deep(.el-select) {
    width: 100%;
  }
}

.flex-display {
  display: flex;
  align-items: center;
  width: 100%;

  label {
    margin-bottom: 0;
  }
}

.switch {
  margin-right: 10px;
}

.gap-between {
  gap: 10px;
}

.number-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;

  .header-key {
    flex: 1;
  }

  .header-value {
    flex: 2;
  }
}

.add-header {
  margin-top: 4px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

:deep(.el-switch.is-checked .el-switch__core) {
  background-color: #5993FF !important;
  border-color: #5993FF !important;
}
</style>
